/**
 * Grupos de turmas — rascunho local e aplicação em lote. Funções PURAS (sem React, sem axios).
 *
 * MODELO: o rascunho é o ESTADO DESEJADO COMPLETO da árvore de grupos; as mudanças são o diff entre
 * ele e o último estado carregado do servidor (`base`). Nada daqui chama a API, exceto `aplicar`, que
 * recebe o objeto `api` por parâmetro (a tela passa o apiService; os testes passam um simulado).
 *
 * Grupo: { id, name, color, tags: string[], parent_id, sort_order, salesman_visibility, min_active, cohorts: number[] }.
 * `salesman_visibility` e `min_active` null = herda do grupo pai (opções avançadas).
 * `allow_mixed_programs` (boolean, por grupo, padrão false): sem ele o grupo, com tudo que está abaixo, só
 * aceita turmas do MESMO programa. `allow_mixed_phases` (boolean, por grupo, padrão false): o ranking do grupo
 * compara só quem está na MESMA fase de quem pede; ligada, inclui todos (a regra roda no ranking, não aqui).
 * As funções de programa recebem `programaDe(cohortId) -> programId`.
 * `id` numérico = existe no servidor; `id` texto ("tmp-1") = grupo novo, ainda sem id real.
 *
 * As regras de tag, ciclo e profundidade repetem as da API (cohort_groups.py) só para responder na
 * hora, sem ida ao servidor; a API continua sendo quem decide ao salvar.
 */

export const MAX_NIVEIS = 5;
export const MAX_TAGS = 8;
export const MAX_TAG_LEN = 40;

export const ehTemp = (id) => typeof id === 'string';

/* ------------------------------------------------------------------ */
/* Tags                                                                */
/* ------------------------------------------------------------------ */

const semAcento = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '');

export function normalizarTag(raw) {
    return semAcento(raw).toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_-]/g, '');
}

/** Lista normalizada, sem vazias nem repetidas. Aceita lista ou texto separado por vírgula/ponto e vírgula. */
export function normalizarTags(raw) {
    if (raw == null) return [];
    const itens = Array.isArray(raw) ? raw : String(raw).split(/[;,]/);
    const out = [];
    itens.forEach((item) => {
        const t = normalizarTag(item);
        if (!t) return;
        if (t.length > MAX_TAG_LEN) throw new Error(`Cada tag pode ter até ${MAX_TAG_LEN} caracteres`);
        if (!out.includes(t)) out.push(t);
    });
    if (out.length > MAX_TAGS) throw new Error(`No máximo ${MAX_TAGS} tags por grupo (vieram ${out.length})`);
    return out;
}

/* ------------------------------------------------------------------ */
/* Opções avançadas (herdam do grupo pai quando vazias)                */
/* ------------------------------------------------------------------ */

export const VISIBILIDADES = ['full', 'initials', 'anonymous'];

/** '' / null = herdar (null); full | initials | anonymous; qualquer outra coisa é erro. */
export function normalizarVisibilidade(v) {
    if (v == null || v === '') return null;
    const x = String(v).trim().toLowerCase();
    if (!VISIBILIDADES.includes(x)) throw new Error('Nome do vendedor: escolha herdar, nome completo, só iniciais ou anônimo');
    return x;
}

/** vazio ou 0 = herdar (null); inteiro >= 1; qualquer outra coisa é erro. */
export function normalizarMinimo(v) {
    if (v == null || String(v).trim() === '') return null;
    const n = Number(v);
    if (!Number.isInteger(n) || n < 0) throw new Error('O mínimo de vendedores deve ser um número inteiro, 1 ou mais (vazio herda do grupo pai)');
    return n === 0 ? null : n;
}

/* ------------------------------------------------------------------ */
/* Leitura da árvore                                                   */
/* ------------------------------------------------------------------ */

export const comparaIrmaos = (a, b) => (a.sort_order - b.sort_order)
    || String(a.name).toLowerCase().localeCompare(String(b.name).toLowerCase(), 'pt-BR')
    || String(a.id).localeCompare(String(b.id), undefined, { numeric: true });

export const clonar = (grupos) => grupos.map((g) => ({ ...g, tags: [...(g.tags || [])], cohorts: [...(g.cohorts || [])] }));

/** Formato do /cohort-groups/list (com include_cohorts) para o formato do rascunho. */
export const normalizarDaApi = (lista) => (lista || []).map((g) => ({
    id: g.id,
    name: g.name,
    color: g.color ? String(g.color).toUpperCase() : null,
    tags: [...(g.tags || [])],
    parent_id: g.parent_id == null ? null : g.parent_id,
    sort_order: g.sort_order || 0,
    salesman_visibility: g.salesman_visibility || null,
    min_active: g.min_active == null ? null : Number(g.min_active),
    allow_mixed_programs: !!g.allow_mixed_programs,
    allow_mixed_phases: !!g.allow_mixed_phases,
    cohorts: (g.cohorts || []).map((c) => c.id),
}));

export const porId = (grupos) => Object.fromEntries(grupos.map((g) => [g.id, g]));

export const filhosDe = (grupos, paiId) => grupos
    .filter((g) => (g.parent_id == null ? null : g.parent_id) === paiId)
    .sort(comparaIrmaos);

export function descendentes(grupos, id) {
    const out = new Set();
    const fila = [id];
    while (fila.length) {
        const atual = fila.shift();
        grupos.forEach((g) => { if (g.parent_id === atual && !out.has(g.id)) { out.add(g.id); fila.push(g.id); } });
    }
    return out;
}

export function profundidade(grupos, id) {
    const m = porId(grupos);
    let n = 0;
    let atual = m[id];
    while (atual && n < 12) { n += 1; atual = atual.parent_id == null ? null : m[atual.parent_id]; }
    return n;
}

export function altura(grupos, id) {
    const h = (g) => 1 + Math.max(0, ...filhosDe(grupos, g).map((f) => h(f.id)));
    return h(id);
}

/* ------------------------------------------------------------------ */
/* Programas: por padrão um grupo só aceita turmas do MESMO programa     */
/* (espelha subtree_programs / mixed_violation da API; a API decide)    */
/* ------------------------------------------------------------------ */

export const msgMesmoPrograma = (nome) => `O grupo '${nome}' só aceita turmas do mesmo programa. Ative 'Permitir programas diferentes' nas opções avançadas dele, ou use turmas do mesmo programa.`;
export const MSG_DESLIGAR_MISTURA = "O grupo já tem turmas de programas diferentes. Tire as turmas de um dos programas antes de desligar 'Permitir programas diferentes'";

/** Programas das turmas do grupo e de tudo abaixo dele. */
export function programasDoGrupo(grupos, id, programaDe) {
    const out = new Set();
    const alvo = [id, ...descendentes(grupos, id)];
    grupos.filter((g) => alvo.includes(g.id)).forEach((g) => g.cohorts.forEach((c) => {
        const p = programaDe(c);
        if (p != null) out.add(p);
    }));
    return out;
}

const ancestraisDe = (grupos, id) => {
    const m = porId(grupos);
    const out = [];
    let atual = m[id] && m[id].parent_id;
    while (atual != null && m[atual] && out.length < 12) { out.push(atual); atual = m[atual].parent_id; }
    return out;
};

/** Primeiro grupo (o próprio ou um ancestral) que NÃO permite programas diferentes e passaria a misturar com `extras`. */
export function violacaoAoIncluir(grupos, grupoId, cohortIds, programaDe) {
    const m = porId(grupos);
    const extras = new Set(cohortIds.map(programaDe).filter((p) => p != null));
    for (const g of [grupoId, ...ancestraisDe(grupos, grupoId)]) {
        if (m[g] && !m[g].allow_mixed_programs) {
            const uniao = new Set([...programasDoGrupo(grupos, g, programaDe), ...extras]);
            if (uniao.size > 1) return g;
        }
    }
    return null;
}

/** Idem para MOVER `id` (com a subárvore) para baixo de `novoPai`: o novo pai e os ancestrais dele passam a conter as turmas dele. */
export function violacaoAoMover(grupos, id, novoPai, programaDe) {
    if (novoPai == null) return null;
    const depois = grupos.map((g) => (g.id === id ? { ...g, parent_id: novoPai } : g));
    const m = porId(depois);
    for (const g of [novoPai, ...ancestraisDe(depois, novoPai)]) {
        if (m[g] && !m[g].allow_mixed_programs && programasDoGrupo(depois, g, programaDe).size > 1) return g;
    }
    return null;
}

/* ------------------------------------------------------------------ */
/* Edições locais (todas devolvem um rascunho NOVO; erro = Error com mensagem em português)           */
/* ------------------------------------------------------------------ */

let sequenciaTemp = 0;
export const novoIdTemp = () => { sequenciaTemp += 1; return `tmp-${sequenciaTemp}`; };

export function adicionarGrupo(rasc, { name, color, tags, parent_id = null, salesman_visibility = null, min_active = null, allow_mixed_programs = false, allow_mixed_phases = false }) {
    const nome = String(name || '').trim();
    if (!nome) throw new Error('Dê um nome ao grupo');
    const lista = normalizarTags(tags);
    const visib = normalizarVisibilidade(salesman_visibility);
    const minimo = normalizarMinimo(min_active);
    const pai = parent_id == null ? null : parent_id;
    if (pai != null && !rasc.some((g) => g.id === pai)) throw new Error('Grupo pai não encontrado');
    if ((pai == null ? 0 : profundidade(rasc, pai)) + 1 > MAX_NIVEIS) throw new Error(`A árvore passaria de ${MAX_NIVEIS} níveis`);
    const id = novoIdTemp();
    const g = { id, name: nome, color: color ? String(color).toUpperCase() : null, tags: lista, parent_id: pai,
        sort_order: filhosDe(rasc, pai).length, salesman_visibility: visib, min_active: minimo, allow_mixed_programs: !!allow_mixed_programs, allow_mixed_phases: !!allow_mixed_phases, cohorts: [] };
    return { rasc: [...rasc, g], id };
}

export function editarGrupo(rasc, id, { name, color, tags, salesman_visibility, min_active, allow_mixed_programs, allow_mixed_phases }, programaDe = null) {
    const nome = String(name == null ? '' : name).trim();
    if (name !== undefined && !nome) throw new Error('Dê um nome ao grupo');
    const lista = tags === undefined ? undefined : normalizarTags(tags);
    const visib = salesman_visibility === undefined ? undefined : normalizarVisibilidade(salesman_visibility);
    const minimo = min_active === undefined ? undefined : normalizarMinimo(min_active);
    const atual = rasc.find((g) => g.id === id);
    if (allow_mixed_programs === false && atual && atual.allow_mixed_programs && programaDe
        && programasDoGrupo(rasc, id, programaDe).size > 1) throw new Error(MSG_DESLIGAR_MISTURA);
    return rasc.map((g) => (g.id !== id ? g : {
        ...g,
        name: name === undefined ? g.name : nome,
        color: color === undefined ? g.color : (color ? String(color).toUpperCase() : null),
        tags: lista === undefined ? g.tags : lista,
        salesman_visibility: visib === undefined ? (g.salesman_visibility || null) : visib,
        min_active: minimo === undefined ? (g.min_active == null ? null : g.min_active) : minimo,
        allow_mixed_programs: allow_mixed_programs === undefined ? !!g.allow_mixed_programs : !!allow_mixed_programs,
        allow_mixed_phases: allow_mixed_phases === undefined ? !!g.allow_mixed_phases : !!allow_mixed_phases,
    }));
}

/** Põe o grupo sob `novoPai` (null = nível principal), na posição `indice` (null = no fim). Renumera os irmãos de destino. */
export function moverGrupo(rasc, id, novoPai, indice = null, programaDe = null) {
    const g = rasc.find((x) => x.id === id);
    if (!g) throw new Error('Grupo não encontrado');
    const pai = novoPai == null ? null : novoPai;
    if (pai != null && (pai === id || descendentes(rasc, id).has(pai))) {
        throw new Error('Não dá para colocar um grupo dentro dele mesmo ou de um grupo abaixo dele');
    }
    if ((pai == null ? 0 : profundidade(rasc, pai)) + altura(rasc, id) > MAX_NIVEIS) {
        throw new Error(`A árvore passaria de ${MAX_NIVEIS} níveis`);
    }
    if (programaDe && pai !== (g.parent_id == null ? null : g.parent_id)) {
        const viol = violacaoAoMover(rasc, id, pai, programaDe);
        if (viol != null) throw new Error(msgMesmoPrograma((rasc.find((x) => x.id === viol) || {}).name));
    }
    const irmaos = filhosDe(rasc, pai).filter((x) => x.id !== id).map((x) => x.id);
    irmaos.splice(indice == null ? irmaos.length : Math.min(Math.max(indice, 0), irmaos.length), 0, id);
    return rasc.map((x) => {
        if (x.id === id) return { ...x, parent_id: pai, sort_order: irmaos.indexOf(id) };
        const i = irmaos.indexOf(x.id);
        return i >= 0 ? { ...x, sort_order: i } : x;
    });
}

export function apagarGrupo(rasc, id) {
    if (rasc.some((g) => g.parent_id === id)) throw new Error('O grupo tem subgrupos. Mova ou apague os subgrupos primeiro');
    return rasc.filter((g) => g.id !== id);
}

export function incluirTurma(rasc, grupoId, cohortId, programaDe = null) {
    if (programaDe) {
        const g0 = rasc.find((g) => g.id === grupoId);
        if (g0 && !g0.cohorts.includes(cohortId)) {
            const viol = violacaoAoIncluir(rasc, grupoId, [cohortId], programaDe);
            if (viol != null) throw new Error(msgMesmoPrograma((rasc.find((x) => x.id === viol) || {}).name));
        }
    }
    return rasc.map((g) => (g.id !== grupoId || g.cohorts.includes(cohortId) ? g : { ...g, cohorts: [...g.cohorts, cohortId] }));
}

export function tirarTurma(rasc, grupoId, cohortId) {
    return rasc.map((g) => (g.id !== grupoId ? g : { ...g, cohorts: g.cohorts.filter((c) => c !== cohortId) }));
}

/** Troca ids temporários pelos reais (depois de criar no servidor). */
export function remapear(rasc, mapa) {
    const r = (id) => (id != null && mapa[id] != null ? mapa[id] : id);
    return rasc.map((g) => ({ ...g, id: r(g.id), parent_id: r(g.parent_id) }));
}

/* ------------------------------------------------------------------ */
/* Diff                                                                */
/* ------------------------------------------------------------------ */

const mesmasTags = (a, b) => a.length === b.length && a.every((t, i) => t === b[i]);

/**
 * Mudanças = desejado − servidor. Cada lista já vem na ORDEM em que deve ser aplicada:
 * novos (pais antes dos filhos) e apagados (folhas antes dos pais, pela árvore do servidor).
 */
export function calcularMudancas(base, rasc) {
    const b = porId(base);
    const r = porId(rasc);

    const novos = rasc.filter((g) => ehTemp(g.id))
        .map((g) => ({ g, nivel: profundidade(rasc, g.id) }))
        .sort((x, y) => x.nivel - y.nivel)
        .map((x) => x.g);

    const apagados = base.filter((g) => !r[g.id])
        .map((g) => ({ g, nivel: profundidade(base, g.id) }))
        .sort((x, y) => y.nivel - x.nivel)
        .map((x) => x.g);

    const editados = [];
    rasc.filter((g) => !ehTemp(g.id) && b[g.id]).forEach((g) => {
        const o = b[g.id];
        const campos = {};
        const quais = [];
        if (g.name !== o.name) { campos.name = g.name; quais.push('nome'); }
        if ((g.color || null) !== (o.color || null)) { campos.color = g.color || ''; quais.push('cor'); }
        if (!mesmasTags(g.tags, o.tags)) { campos.tags = g.tags; quais.push('tags'); }
        if ((g.salesman_visibility || null) !== (o.salesman_visibility || null)) {
            campos.salesman_visibility = g.salesman_visibility || null; quais.push('visibilidade');
        }
        if ((g.min_active || null) !== (o.min_active || null)) { campos.min_active = g.min_active || null; quais.push('minimo'); }
        if (!!g.allow_mixed_programs !== !!o.allow_mixed_programs) { campos.allow_mixed_programs = !!g.allow_mixed_programs; quais.push('programas'); }
        if (!!g.allow_mixed_phases !== !!o.allow_mixed_phases) { campos.allow_mixed_phases = !!g.allow_mixed_phases; quais.push('fases'); }
        const mudouPai = g.parent_id !== o.parent_id;
        const mudouOrdem = !mudouPai && g.sort_order !== o.sort_order;
        if (mudouPai || g.sort_order !== o.sort_order) campos.sort_order = g.sort_order;
        if (mudouPai) campos.parent_id = g.parent_id;
        if (quais.length || mudouPai || mudouOrdem) {
            editados.push({ g, antes: o, campos, quais, mudouPai, mudouOrdem });
        }
    });

    const incluidas = [];
    const retiradas = [];
    rasc.forEach((g) => {
        const o = b[g.id];
        g.cohorts.forEach((c) => { if (!o || !o.cohorts.includes(c)) incluidas.push({ grupo: g, cohort_id: c }); });
        if (o) o.cohorts.forEach((c) => { if (!g.cohorts.includes(c)) retiradas.push({ grupo: g, cohort_id: c }); });
    });

    const dados = editados.filter((e) => e.quais.length).length;
    const movidos = editados.filter((e) => e.mudouPai).length;
    const ordem = editados.filter((e) => e.mudouOrdem).length;
    const total = novos.length + apagados.length + dados + movidos + ordem + incluidas.length + retiradas.length;
    return { novos, apagados, editados, incluidas, retiradas, total };
}

/* ------------------------------------------------------------------ */
/* Aplicação em lote (ordem segura)                                    */
/* ------------------------------------------------------------------ */

const detalhe = (e) => e?.response?.data?.detail || e?.message || 'Erro desconhecido';

/**
 * Aplica as mudanças pelas rotas /cohort-groups/*. Ordem (pensada nas regras da API):
 *   A. criar grupos novos, pais antes dos filhos (ids temporários -> reais);
 *   B. editar grupos existentes; as que LIGAM "programas diferentes" vão primeiro;
 *   C. retirar turmas (antes de desligar uma opção ou mover um grupo para um pai que não aceita mistura);
 *   D. editar os grupos que DESLIGAM "programas diferentes" (a API recusa desligar com mistura existente);
 *   E. incluir turmas (depois de ligar a opção);
 *   F. apagar grupos, folhas primeiro.
 * Mudança de pai e mudança da opção de programas podem depender de outro passo que vem depois (ciclo,
 * profundidade, mistura): se a API recusar, é ADIADA e tentada de novo quando algo mais foi aplicado;
 * sem progresso, para com a mensagem da API. Qualquer outra falha PARA na hora.
 * Devolve { ok, mapa, falha: { rotulo, mensagem }, aplicados }.
 * O chamador recarrega o servidor, remapeia o rascunho com `mapa` e recalcula: o que já foi aplicado
 * some do diff, então nada se repete.
 */
export async function aplicar({ api, subdomain, base, rasc, vigencia, turmas = [], aoPasso }) {
    const m = calcularMudancas(base, rasc);
    const mapa = {};
    let aplicados = 0;
    const real = (id) => (ehTemp(id) ? mapa[id] : id);
    const nomeDe = (id) => (rasc.find((g) => g.id === id) || base.find((g) => g.id === id) || {}).name || `grupo ${id}`;
    const nomeTurma = (id) => (turmas.find((t) => t.id === id) || {}).name || `turma ${id}`;

    const rodar = async (rotulo, fn) => {
        if (aoPasso) aoPasso(rotulo);
        try {
            await fn();
            aplicados += 1;
            return { ok: true };
        } catch (e) {
            return { ok: false, falha: { rotulo, mensagem: detalhe(e) } };
        }
    };
    const falhou = (res) => ({ ok: false, mapa, aplicados, falha: res.falha });

    // A. criar
    for (const g of m.novos) {
        // eslint-disable-next-line no-await-in-loop
        const res = await rodar(`Criar o grupo "${g.name}"`, async () => {
            const corpo = { subdomain, name: g.name, color: g.color || '', tags: g.tags.join(', '),
                sort_order: g.sort_order, effective_on: vigencia };
            if (g.salesman_visibility) corpo.salesman_visibility = g.salesman_visibility;
            if (g.min_active) corpo.min_active = g.min_active;
            if (g.allow_mixed_programs) corpo.allow_mixed_programs = true;
            if (g.allow_mixed_phases) corpo.allow_mixed_phases = true;
            const pai = real(g.parent_id);
            if (pai != null) corpo.parent_id = pai;
            const r = await api.cohortGroupSave(corpo);
            mapa[g.id] = r?.data?.id;
            if (mapa[g.id] == null) throw new Error('A API não devolveu o id do grupo criado');
        });
        if (!res.ok) return falhou(res);
    }

    // passos de edição (só o que mudou; null = herdar)
    const passoEditar = (e) => ({
        adiavel: e.mudouPai || 'allow_mixed_programs' in e.campos,
        rotulo: `Salvar o grupo "${e.g.name}"`,
        fn: async () => {
            const corpo = { subdomain, id: e.g.id, effective_on: vigencia };
            if ('name' in e.campos) corpo.name = e.campos.name;
            if ('color' in e.campos) corpo.color = e.campos.color;
            if ('tags' in e.campos) corpo.tags = e.campos.tags.join(', ');
            if ('salesman_visibility' in e.campos) corpo.salesman_visibility = e.campos.salesman_visibility;   // null = herdar
            if ('min_active' in e.campos) corpo.min_active = e.campos.min_active;                               // null = herdar
            if ('allow_mixed_programs' in e.campos) corpo.allow_mixed_programs = e.campos.allow_mixed_programs;
            if ('allow_mixed_phases' in e.campos) corpo.allow_mixed_phases = e.campos.allow_mixed_phases;
            if ('sort_order' in e.campos) corpo.sort_order = e.campos.sort_order;
            if (e.mudouPai) corpo.parent_id = real(e.campos.parent_id) ?? null;
            await api.cohortGroupSave(corpo);
        },
    });
    const adiados = [];
    const executarPasso = async (passo) => {
        const res = await rodar(passo.rotulo, passo.fn);
        if (res.ok) return null;
        if (passo.adiavel) { adiados.push({ passo, res }); return null; }
        return falhou(res);
    };
    /** Tenta de novo os adiados enquanto houver progresso. */
    const reprocessar = async () => {
        let progresso = true;
        while (adiados.length && progresso) {
            progresso = false;
            for (const item of [...adiados]) {
                // eslint-disable-next-line no-await-in-loop
                const res = await rodar(item.passo.rotulo, item.passo.fn);
                if (res.ok) { adiados.splice(adiados.indexOf(item), 1); progresso = true; } else item.res = res;
            }
        }
    };

    const desligam = m.editados.filter((e) => e.campos.allow_mixed_programs === false);
    const demais = m.editados.filter((e) => e.campos.allow_mixed_programs !== false)
        .sort((x, y) => (y.campos.allow_mixed_programs === true) - (x.campos.allow_mixed_programs === true));

    // B. editar (ligar programas diferentes primeiro)
    for (const e of demais) {
        // eslint-disable-next-line no-await-in-loop
        const f = await executarPasso(passoEditar(e));
        if (f) return f;
    }

    // C. retirar turmas
    for (const x of m.retiradas) {
        // eslint-disable-next-line no-await-in-loop
        const res = await rodar(`Tirar a turma "${nomeTurma(x.cohort_id)}" de "${x.grupo.name}"`,
            () => api.cohortGroupMemberRemove({ subdomain, group_id: real(x.grupo.id), cohort_id: x.cohort_id, effective_on: vigencia }));
        if (!res.ok) return falhou(res);
    }
    await reprocessar();

    // D. desligar programas diferentes (já sem a mistura)
    for (const e of desligam) {
        // eslint-disable-next-line no-await-in-loop
        const f = await executarPasso(passoEditar(e));
        if (f) return f;
    }
    await reprocessar();
    if (adiados.length) return falhou(adiados[0].res);

    // E. incluir turmas
    for (const x of m.incluidas) {
        // eslint-disable-next-line no-await-in-loop
        const res = await rodar(`Incluir a turma "${nomeTurma(x.cohort_id)}" em "${x.grupo.name}"`,
            () => api.cohortGroupMemberAdd({ subdomain, group_id: real(x.grupo.id), cohort_id: x.cohort_id, effective_on: vigencia }));
        if (!res.ok) return falhou(res);
    }

    // F. apagar (folhas primeiro)
    for (const g of m.apagados) {
        // eslint-disable-next-line no-await-in-loop
        const res = await rodar(`Apagar o grupo "${nomeDe(g.id)}"`,
            () => api.cohortGroupDelete({ subdomain, id: g.id, effective_on: vigencia }));
        if (!res.ok) return falhou(res);
    }
    return { ok: true, mapa, aplicados, falha: null };
}
